import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import EuroIcon from '@mui/icons-material/Euro';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import TimelineIcon from '@mui/icons-material/Timeline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PaidIcon from '@mui/icons-material/Paid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import { useUserPreferences } from '../../global/useUserPreferences';
import { getPeriodRange, matchPreset } from './periodPresets';
import { useDocumentOverview } from './useDocumentOverview';
import { getResourceLabels } from './resourceLabels';
import MiniStat from './MiniStat';
import PeriodSelector from './PeriodSelector';
import OverviewActions from './OverviewActions';
import StatusFilterChips from './StatusFilterChips';
import KpiGrid from './KpiGrid';
import KpiTile from './KpiTile';
import StatusBreakdownDonut from './StatusBreakdownDonut';
import TopClientsList from './TopClientsList';
import AgingBuckets from './specials/AgingBuckets';
import ConversionFunnel from './specials/ConversionFunnel';
import ContractRevenue from './specials/ContractRevenue';

const DEBOUNCE_MS = 300;

const HAS_OPEN_KPIS = {
  orders: true,
  invoices: true,
  offers: true,
  contracts: false,
};

// Status distribution donut: orders + invoices. Offers have the funnel instead;
// contracts hide it (status mapping is not meaningful there).
const HAS_STATUS_DONUT = {
  orders: true,
  offers: false,
  invoices: true,
  contracts: false,
};

const DocumentOverviewCardInner = ({
  resource,
  preferencesKey,
  defaultStatuses = null,
  defaultPeriod = 'total',
  onFilterChange,
  actions = [],
  preferences,
  updatePreferences,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const prefKey = preferencesKey ?? resource;
  const labels = getResourceLabels(resource);
  const showOpen = HAS_OPEN_KPIS[resource];
  const showStatusDonut = HAS_STATUS_DONUT[resource];

  // preferences is guaranteed loaded here (wrapper waits). State is initialized
  // synchronously from stored prefs — no async hydration effect, so the first
  // onFilterChange already carries the persisted filter (no double fetch).
  const stored = preferences?.documentOverview?.[prefKey];
  const [period, setPeriod] = useState(() => {
    const range =
      stored?.key === 'custom'
        ? { from: stored.from ?? null, to: stored.to ?? null }
        : getPeriodRange(stored?.period ?? defaultPeriod);
    return {
      key: stored?.period ?? defaultPeriod,
      from: stored?.from !== undefined ? stored.from : range.from,
      to: stored?.to !== undefined ? stored.to : range.to,
    };
  });
  const [statuses, setStatuses] = useState(
    stored?.statuses === undefined ? defaultStatuses : stored.statuses,
  );
  const [amountMode, setAmountMode] = useState(stored?.amountMode ?? 'net');
  const [expanded, setExpanded] = useState(stored?.expanded ?? false);

  // Debounced persistence — skip the very first run (state == stored already).
  const persistTimerRef = useRef(null);
  const firstPersistRef = useRef(true);
  useEffect(() => {
    if (firstPersistRef.current) {
      firstPersistRef.current = false;
      return;
    }
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      const nextDocOverview = {
        ...(preferences?.documentOverview ?? {}),
        [prefKey]: {
          period: period.key,
          from: period.from,
          to: period.to,
          statuses,
          amountMode,
          expanded,
        },
      };
      updatePreferences('documentOverview', nextDocOverview);
    }, DEBOUNCE_MS);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.key, period.from, period.to, statuses?.join(','), amountMode, expanded, prefKey]);

  // Notify parent (drives the table filter). Fires once on mount with the
  // correct (hydrated) filter, then on every change.
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange({ statuses, dateFrom: period.from, dateTo: period.to });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.from, period.to, statuses?.join(',')]);

  const { data, isLoading } = useDocumentOverview({
    resource,
    dateFrom: period.from,
    dateTo: period.to,
    statuses,
  });

  const trend = data?.trend ?? null;
  const statusBreakdown = useMemo(() => data?.statusBreakdown ?? [], [data]);
  const topClients = data?.topClients ?? [];
  const specials = data?.specials ?? null;

  const isNet = amountMode === 'net';
  const volumeTrend = isNet ? trend?.deltas?.net : trend?.deltas?.gross;
  const volumeSuffix = isNet ? t('netto') : t('brutto');
  const withSuffix = (label) => `${label} (${volumeSuffix})`;

  // KPI volumes reflect the active status filter: when statuses are selected the
  // card sums only those statuses, so the totals track the table/filter (a null
  // or empty selection means "all" and sums the full breakdown). The backend
  // always returns the full per-period breakdown, so the filtering happens here.
  // Respects the netto/brutto toggle.
  const vols = useMemo(() => {
    const hasFilter = Array.isArray(statuses) && statuses.length > 0;
    const rows = hasFilter
      ? statusBreakdown.filter((b) => statuses.includes(b.status))
      : statusBreakdown;
    const v = (b) => ((isNet ? b.net : b.gross) ?? 0);
    const ov = (b) => ((isNet ? b.openNet : b.openGross) ?? 0);
    const sum = (keys) =>
      rows.filter((b) => keys.includes(b.key)).reduce((a, b) => a + v(b), 0);
    const sumOpen = (keys) =>
      rows.filter((b) => keys.includes(b.key)).reduce((a, b) => a + ov(b), 0);
    const total = rows.reduce((a, b) => a + v(b), 0);
    const totalOpen = rows.reduce((a, b) => a + ov(b), 0);
    const count = rows.reduce((a, b) => a + (b.count ?? 0), 0);
    return { sum, sumOpen, total, totalOpen, count, avg: count > 0 ? total / count : 0 };
  }, [statusBreakdown, isNet, statuses]);

  // Contract recurring-revenue figures come from the special block (net/gross).
  const contractVals = useMemo(() => {
    const s = specials ?? {};
    return {
      monthly: isNet ? (s.monthlyFixedNet ?? 0) : (s.monthlyFixedGross ?? 0),
      annual: isNet ? (s.annualizedNet ?? 0) : (s.annualizedGross ?? 0),
      invoiced: isNet ? (s.invoiceTotalNet ?? 0) : (s.invoiceTotalGross ?? 0),
    };
  }, [specials, isNet]);

  const countKpi = {
    key: 'count',
    label: t('Document count'),
    value: vols.count,
    type: 'number',
    icon: <DescriptionIcon fontSize="small" />,
    trend: trend?.deltas?.count,
  };
  const avgKpi = {
    key: 'avg',
    label: withSuffix(t('Avg. value')),
    value: vols.avg,
    icon: <TimelineIcon fontSize="small" />,
  };

  const kpis = useMemo(() => {
    if (resource === 'offers') {
      return [
        { key: 'offered', label: withSuffix(t('Offered')), value: vols.total, icon: <DescriptionIcon fontSize="small" />, trend: volumeTrend },
        { key: 'pending', label: withSuffix(t('Pending')), value: vols.sum(['CREATED', 'MAIL_SENT']), icon: <HourglassEmptyIcon fontSize="small" /> },
        { key: 'accepted', label: withSuffix(t('Accepted')), value: vols.sum(['ACCEPTED', 'FINISHED']), icon: <CheckCircleIcon fontSize="small" /> },
        { key: 'rejected', label: withSuffix(t('Rejected')), value: vols.sum(['REJECTED']), icon: <CancelIcon fontSize="small" /> },
        avgKpi,
        countKpi,
      ];
    }
    if (resource === 'orders') {
      return [
        { key: 'volume', label: withSuffix(t(labels.volumeLabel)), value: vols.total, icon: <EuroIcon fontSize="small" />, trend: volumeTrend },
        { key: 'open', label: withSuffix(t('Open')), value: vols.totalOpen, icon: <HourglassEmptyIcon fontSize="small" /> },
        { key: 'billed', label: withSuffix(t('Billed')), value: vols.total - vols.totalOpen, icon: <CheckCircleIcon fontSize="small" /> },
        avgKpi,
        countKpi,
      ];
    }
    if (resource === 'invoices') {
      return [
        { key: 'revenue', label: withSuffix(t(labels.volumeLabel)), value: vols.total, icon: <EuroIcon fontSize="small" />, trend: volumeTrend },
        { key: 'paid', label: withSuffix(t('Paid')), value: vols.total - vols.totalOpen, icon: <PaidIcon fontSize="small" /> },
        { key: 'open', label: withSuffix(t('Open')), value: vols.totalOpen, icon: <HourglassEmptyIcon fontSize="small" /> },
        { key: 'overdue', label: withSuffix(t('Overdue')), value: vols.sumOpen(['IN_ARREARS']), icon: <QueryBuilderIcon fontSize="small" /> },
        avgKpi,
        countKpi,
      ];
    }
    if (resource === 'contracts') {
      return [
        { key: 'monthly', label: withSuffix(t('Monthly fixed')), value: contractVals.monthly, icon: <AutorenewIcon fontSize="small" /> },
        { key: 'annual', label: withSuffix(t('Annualized')), value: contractVals.annual, icon: <TimelineIcon fontSize="small" /> },
        { key: 'invoiced', label: withSuffix(t('Invoiced in period')), value: contractVals.invoiced, icon: <PaidIcon fontSize="small" /> },
        countKpi,
      ];
    }
    return [countKpi, avgKpi];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, labels, resource, vols, contractVals, volumeTrend, volumeSuffix, trend]);

  // Compact stats shown in the collapsed bar (subset of the KPIs).
  const collapsedStats = useMemo(() => {
    if (resource === 'offers') {
      return [
        { key: 'offered', label: t('Offered'), value: vols.total, type: 'money', trend: volumeTrend },
        { key: 'pending', label: t('Pending'), value: vols.sum(['CREATED', 'MAIL_SENT']), type: 'money' },
        { key: 'accepted', label: t('Accepted'), value: vols.sum(['ACCEPTED', 'FINISHED']), type: 'money' },
        { key: 'count', label: t('Document count'), value: vols.count, type: 'number', trend: trend?.deltas?.count },
      ];
    }
    if (resource === 'orders') {
      return [
        { key: 'volume', label: t(labels.volumeLabel), value: vols.total, type: 'money', trend: volumeTrend },
        { key: 'open', label: t('Open'), value: vols.totalOpen, type: 'money' },
        { key: 'billed', label: t('Billed'), value: vols.total - vols.totalOpen, type: 'money' },
        { key: 'count', label: t('Document count'), value: vols.count, type: 'number', trend: trend?.deltas?.count },
      ];
    }
    if (resource === 'invoices') {
      return [
        { key: 'revenue', label: t(labels.volumeLabel), value: vols.total, type: 'money', trend: volumeTrend },
        { key: 'paid', label: t('Paid'), value: vols.total - vols.totalOpen, type: 'money' },
        { key: 'open', label: t('Open'), value: vols.totalOpen, type: 'money' },
        { key: 'count', label: t('Document count'), value: vols.count, type: 'number', trend: trend?.deltas?.count },
      ];
    }
    if (resource === 'contracts') {
      return [
        { key: 'monthly', label: t('Monthly fixed'), value: contractVals.monthly, type: 'money' },
        { key: 'invoiced', label: t('Invoiced in period'), value: contractVals.invoiced, type: 'money' },
        { key: 'count', label: t('Document count'), value: vols.count, type: 'number', trend: trend?.deltas?.count },
      ];
    }
    return [
      { key: 'count', label: t('Document count'), value: vols.count, type: 'number', trend: trend?.deltas?.count },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, labels, resource, vols, contractVals, volumeTrend, trend]);

  // Period label for the collapsed pill.
  const periodLabel = useMemo(() => {
    const matched = matchPreset(period.from, period.to);
    if (matched) return t(matched.labelKey);
    if (period.from && period.to) {
      const fmt = (iso) => {
        const [y, m, d] = iso.split('-');
        return `${d}.${m}.${y.slice(2)}`;
      };
      return `${fmt(period.from)} – ${fmt(period.to)}`;
    }
    return t('Total');
  }, [period.from, period.to, t]);

  const activeStatusCount = Array.isArray(statuses) ? statuses.length : 0;
  const hasStatusFilter = activeStatusCount > 0;

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${colors.glassFrom}, ${colors.glassTo})`,
        border: `1px solid ${colors.selected}`,
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        padding: { xs: 1, md: 1.25 },
        marginBottom: 2,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 8px 24px -8px rgba(0,0,0,0.5)'
            : '0 8px 24px -8px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: expanded ? 1.75 : 0,
        transition: 'gap 0.2s ease',
      }}
    >
      {/* Always-visible compact summary bar. Click to expand. */}
      <Box
        onClick={() => setExpanded((e) => !e)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          cursor: 'pointer',
          paddingX: 0.5,
          paddingY: 0.25,
          borderRadius: '8px',
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: `${colors.selected}40` },
        }}
      >
        <IconButton size="small" sx={{ padding: 0.25 }} tabIndex={-1}>
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: '1.1rem', color: colors.text }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: '1.1rem', color: colors.text }} />
          )}
        </IconButton>
        <Typography
          variant="overline"
          sx={{
            color: colors.text,
            opacity: 0.6,
            fontSize: '0.7rem',
            letterSpacing: 1,
            lineHeight: 1,
          }}
        >
          {t('Overview')}
        </Typography>

        {/* Mini-stats — only when collapsed (label on top, value + trend below) */}
        {!expanded && (
          <Box
            className="flex items-center flex-1 flex-wrap min-w-0"
            sx={{ gap: { xs: 2, md: 3 }, ml: 1 }}
          >
            {collapsedStats.map((stat) => (
              <MiniStat
                key={stat.key}
                label={stat.label}
                value={stat.value}
                type={stat.type}
                trend={stat.trend}
                isLoading={isLoading}
              />
            ))}
          </Box>
        )}

        {/* Right side: amount toggle (always) + period + filter hint */}
        <Box className="flex items-center gap-2 ml-auto" onClick={(e) => e.stopPropagation()}>
          {!expanded && hasStatusFilter && (
            <Tooltip title={`${activeStatusCount} ${t('Status')}`}>
              <Box
                className="flex items-center gap-0.5"
                sx={{
                  color: colors.info ?? colors.text,
                  fontSize: '0.7rem',
                  opacity: 0.85,
                }}
              >
                <FilterAltIcon sx={{ fontSize: '0.9rem' }} />
                {activeStatusCount}
              </Box>
            </Tooltip>
          )}
          <ToggleButtonGroup
            value={amountMode}
            exclusive
            size="small"
            onChange={(_, v) => v && setAmountMode(v)}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                color: `${colors.text}99`,
                borderColor: colors.selected,
                paddingX: 1,
                paddingY: 0.25,
                fontSize: '0.7rem',
                lineHeight: 1.2,
                '&.Mui-selected': {
                  backgroundColor: colors.selected,
                  color: colors.text,
                },
              },
            }}
          >
            <ToggleButton value="net">{t('netto')}</ToggleButton>
            <ToggleButton value="gross">{t('brutto')}</ToggleButton>
          </ToggleButtonGroup>
          {!expanded && (
            <Typography
              variant="caption"
              sx={{
                color: colors.text,
                opacity: 0.65,
                fontSize: '0.72rem',
                paddingX: 1,
                paddingY: 0.25,
                borderRadius: '999px',
                border: `1px solid ${colors.selected}`,
                whiteSpace: 'nowrap',
              }}
            >
              {periodLabel}
            </Typography>
          )}
          {expanded && (
            <PeriodSelector
              from={period.from}
              to={period.to}
              onChange={(p) => setPeriod(p)}
            />
          )}
          <OverviewActions actions={actions} />
        </Box>
      </Box>

      {/* Expandable body */}
      <Collapse in={expanded} unmountOnExit>
        <Box className="flex flex-col gap-3 pt-1">
          <StatusFilterChips
            resource={resource}
            selectedStatuses={statuses}
            onChange={setStatuses}
            breakdown={statusBreakdown}
          />

          <KpiGrid columns={kpis.length}>
            {kpis.map((k) => (
              <KpiTile
                key={k.key}
                label={k.label}
                value={k.value}
                type={k.type}
                icon={k.icon}
                trend={k.trend}
                isLoading={isLoading}
              />
            ))}
          </KpiGrid>

          {/* Detail blocks (special + status donut + top clients) flow side by
              side in a responsive grid — the status donut sits next to the top
              clients list. */}
          {(specials || showStatusDonut || topClients.length > 0) && (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(auto-fit, minmax(240px, 1fr))',
                },
                alignItems: 'start',
                paddingTop: 1,
                borderTop: `1px solid ${colors.selected}`,
              }}
            >
              {specials?.type === 'aging_buckets' && (
                <AgingBuckets specials={specials} />
              )}
              {specials?.type === 'conversion_funnel' && (
                <ConversionFunnel specials={specials} />
              )}
              {specials?.type === 'contract_revenue' && (
                <ContractRevenue specials={specials} amountMode={amountMode} />
              )}
              {showStatusDonut && !isLoading && (
                <StatusBreakdownDonut
                  resource={resource}
                  breakdown={statusBreakdown}
                  amountMode={amountMode}
                  hideOpenMode={!showOpen}
                />
              )}
              {topClients.length > 0 && (
                <TopClientsList topClients={topClients} amountMode={amountMode} />
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// Wrapper waits for preferences to load before mounting the inner card. This
// guarantees the inner state is initialized synchronously from the persisted
// filter, so the parent scene's table fetch fires exactly once with the right
// filter (instead of once unfiltered + once filtered).
const DocumentOverviewCard = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { preferences, updatePreferences } = useUserPreferences();

  if (preferences === null) {
    return (
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.glassFrom}, ${colors.glassTo})`,
          border: `1px solid ${colors.selected}`,
          backdropFilter: 'blur(12px)',
          borderRadius: '16px',
          padding: { xs: 1, md: 1.25 },
          marginBottom: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Skeleton variant="circular" width={20} height={20} />
        <Skeleton width={280} height={20} />
      </Box>
    );
  }

  return (
    <DocumentOverviewCardInner
      {...props}
      preferences={preferences}
      updatePreferences={updatePreferences}
    />
  );
};

export default DocumentOverviewCard;
